import { withTrueWeightTotal } from "./choiceWeightTotal";

export type RequirementGroup = "documents" | "library" | "suggestions";

export type AnswerChoice = {
  label: string;
  recommended: boolean;
  group?: RequirementGroup;
  source?: string;
};

export type QuestionKind = "choice" | "checklist" | "requirements" | "number" | "date";

export type AnswerQuestion = {
  kind: QuestionKind;
  choices: AnswerChoice[];
  proposal: string | null;
  why: string | null;
  source: string | null;
};

const CHOICE_BLOCKS = /\n?```valg[^\S\n]*\n([\s\S]*?)(?:\n```[^\S\n]*|$)/g;
const RECOMMENDED = /^\[anbefalt\]\s*/i;
const FIELD = /^(type|forslag|hvorfor|kilde):\s*(.*)$/i;
const GROUP = /^gruppe:\s*(.*)$/i;
const OPTION_SOURCE = /\s+\|\s*kilde:\s*(.*)$/i;

const GROUPS: Record<string, RequirementGroup> = {
  dokumenter: "documents",
  bibliotek: "library",
  forslag: "suggestions",
};

const KINDS: Record<string, QuestionKind> = {
  velg: "choice",
  flervalg: "checklist",
  kravliste: "requirements",
  tall: "number",
  dato: "date",
};

const REQUIREMENT_LIST = /^\s*type:\s*kravliste\s*$/im;
const CONTINUATION = /^(?: {2,}|\t)\S/;

type Option = { text: string; group?: RequirementGroup };

const toChoice = ({ text, group }: Option, keepsWording: boolean): AnswerChoice | null => {
  const optionSource = OPTION_SOURCE.exec(text)?.[1]?.trim();
  const withoutMarks = text.replace(OPTION_SOURCE, "").replace(RECOMMENDED, "");
  const label = (keepsWording ? withoutMarks : withTrueWeightTotal(withoutMarks)).trim();
  if (!label) return null;
  return {
    label,
    recommended: RECOMMENDED.test(text),
    ...(group ? { group } : {}),
    ...(optionSource ? { source: optionSource } : {}),
  };
};

export const splitAnswerQuestion = (text: string): { text: string; question: AnswerQuestion | null } => {
  const blocks = [...text.matchAll(CHOICE_BLOCKS)];
  if (blocks.length === 0) return { text, question: null };
  const isRequirementList = blocks.some((block) => REQUIREMENT_LIST.test(block[1]));
  const fields: Record<string, string> = {};
  const options: Option[] = [];
  let group: RequirementGroup | undefined;
  let inGroups = false;
  let lastOption: Option | null = null;
  blocks
    .flatMap((block) => block[1].split("\n"))
    .filter((line) => line.trim())
    .forEach((rawLine) => {
      if (isRequirementList && lastOption && CONTINUATION.test(rawLine)) {
        lastOption.text = `${lastOption.text}\n${rawLine.trim()}`;
        return;
      }
      const line = rawLine.trim();
      lastOption = null;
      const heading = GROUP.exec(line);
      if (heading) {
        group = GROUPS[heading[1].trim().toLowerCase()];
        inGroups = true;
        return;
      }
      const field = inGroups ? null : FIELD.exec(line);
      if (field) {
        const name = field[1].toLowerCase();
        if (!(name in fields)) fields[name] = field[2].trim();
        return;
      }
      lastOption = { text: line.replace(/^[-*]\s*/, "").trim(), group };
      options.push(lastOption);
    });
  const choices = options
    .map((option) => toChoice(option, isRequirementList))
    .filter((choice): choice is AnswerChoice => choice !== null);
  return {
    text: text.replace(CHOICE_BLOCKS, "").replace(/\n{3,}/g, "\n\n").trim(),
    question: {
      kind: KINDS[(fields.type ?? "").toLowerCase()] ?? "choice",
      choices,
      proposal: fields.forslag || null,
      why: fields.hvorfor || null,
      source: fields.kilde || null,
    },
  };
};

export const endsWithQuestionWithoutCard = (text: string): boolean => {
  const { text: prose, question } = splitAnswerQuestion(text);
  if (question) return false;
  const lastLine = prose.trim().split("\n").pop()?.trim() ?? "";
  return lastLine.endsWith("?");
};

export const hasAnswerQuestion = (text: string | null | undefined): boolean =>
  !!text && splitAnswerQuestion(text).question !== null;
